import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { useLocation, useNavigate } from 'react-router-dom';

const CustomerPayments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const invoice = location.state?.invoice;
  const [invoiceFromQuery, setInvoiceFromQuery] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [formData, setFormData] = useState({
    card_number: '',
    expiry_month: '',
    expiry_year: '',
    cvv: '',
    cardholder_name: '',
    amount: invoice?.total_amount || '',
    save_payment_method: true, // Default to saving new payment methods
  });

  // Use invoice from state or from query
  const currentInvoice = invoice || invoiceFromQuery;
  const [loading, setLoading] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Check if invoice_id is in query params (from email link)
    const searchParams = new URLSearchParams(location.search);
    const invoiceId = searchParams.get('invoice_id');
    
    if (invoice) {
      // Invoice passed via state (from navigation)
      fetchPaymentMethods();
    } else if (invoiceId) {
      // Invoice ID in query params (from email link)
      fetchInvoiceById(invoiceId);
    }
  }, [invoice, location.search]);

  const fetchInvoiceById = async (invoiceId) => {
    setLoadingInvoice(true);
    try {
      const response = await api.get(`/invoices/${invoiceId}/`);
      setInvoiceFromQuery(response.data);
      setFormData(prev => ({
        ...prev,
        amount: response.data.total_amount || '',
      }));
      fetchPaymentMethods();
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError('Failed to load invoice. Please try again.');
    } finally {
      setLoadingInvoice(false);
    }
  };

  // Reset selected payment method if it no longer exists in the payment methods list
  useEffect(() => {
    if (loadingMethods) return; // Don't reset while loading
    
    if (selectedPaymentMethodId && paymentMethods.length > 0) {
      const methodExists = paymentMethods.some(m => m.id === selectedPaymentMethodId);
      if (!methodExists && selectedPaymentMethodId !== 'new') {
        // Reset to default or empty if selected method no longer exists
        const defaultMethod = paymentMethods.find(method => method.is_default && method.is_active) || 
                             paymentMethods.find(method => method.is_active) || 
                             null;
        setSelectedPaymentMethodId(defaultMethod?.id || '');
        setDefaultPaymentMethod(defaultMethod);
      }
    } else if (selectedPaymentMethodId && paymentMethods.length === 0 && !loadingMethods) {
      // If payment methods are empty and not loading, reset selection
      setSelectedPaymentMethodId('');
      setDefaultPaymentMethod(null);
    }
  }, [paymentMethods, selectedPaymentMethodId, loadingMethods]);

  const fetchPaymentMethods = async () => {
    if (!currentInvoice) return;
    setLoadingMethods(true);
    try {
      const response = await api.get('/payments/methods/');
      // Handle both direct array responses and paginated responses
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        data = response.data.results;
      }
      
      setPaymentMethods(data);
      
      // Find default payment method (prioritize is_default, then use first available)
      const defaultMethod = data.find(method => method.is_default && method.is_active) || 
                           data.find(method => method.is_active) || 
                           null;
      setDefaultPaymentMethod(defaultMethod);
      
      // Only set selected payment method ID if it exists in the loaded data
      // Also validate that the current selectedPaymentMethodId still exists
      const currentSelected = selectedPaymentMethodId;
      if (currentSelected && data.some(m => m.id === currentSelected)) {
        // Keep the current selection if it still exists
        setSelectedPaymentMethodId(currentSelected);
      } else if (defaultMethod && data.some(m => m.id === defaultMethod.id)) {
        // Use default if current selection doesn't exist
        setSelectedPaymentMethodId(defaultMethod.id);
      } else {
        // Reset to empty if no valid selection
        setSelectedPaymentMethodId('');
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]);
      setDefaultPaymentMethod(null);
      setSelectedPaymentMethodId('');
    } finally {
      setLoadingMethods(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePaymentMethodChange = (e) => {
    const value = e.target.value;
    console.log('Payment method selected:', value); // Debug log
    setSelectedPaymentMethodId(value);
    setShowNewCardForm(value === 'new');
    setError('');
    
    // Clear CVV when switching payment methods
    setFormData(prev => ({
      ...prev,
      cvv: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate that a payment method is selected or new card form is shown
      if (!selectedPaymentMethodId && !showNewCardForm && !defaultPaymentMethod) {
        setError('Please select a payment method or add a new one');
        setLoading(false);
        return;
      }

      let paymentMethodIdToUse = selectedPaymentMethodId || defaultPaymentMethod?.id;

      // If adding new payment method and user wants to save it, create it first
      if (showNewCardForm && formData.save_payment_method) {
        if (!formData.card_number || !formData.expiry_month || !formData.expiry_year || !formData.cvv || !formData.cardholder_name) {
          setError('Please fill in all card details');
          setLoading(false);
          return;
        }

        try {
          // Create the payment method first
          const paymentMethodData = {
            card_number: formData.card_number.replace(/\s/g, ''),
            expiry_month: parseInt(formData.expiry_month),
            expiry_year: parseInt(formData.expiry_year),
            cvv: formData.cvv,
            cardholder_name: formData.cardholder_name,
            is_default: paymentMethods.length === 0, // Set as default if it's the first one
          };

          const methodResponse = await api.post('/payments/methods/', paymentMethodData);
          paymentMethodIdToUse = methodResponse.data.id;
          
          // Refresh payment methods list and update selection
          await fetchPaymentMethods();
          setSelectedPaymentMethodId(paymentMethodIdToUse);
          setShowNewCardForm(false); // Switch to using the saved payment method
        } catch (methodError) {
          console.error('Error saving payment method:', methodError);
          // Continue with payment even if saving method fails
          // User can still pay with the card
        }
      }

      const payload = {
        invoice_id: currentInvoice.id,
        amount: formData.amount,
      };

      if (showNewCardForm) {
        // Using new card data
        if (!formData.card_number || !formData.expiry_month || !formData.expiry_year || !formData.cvv || !formData.cardholder_name) {
          setError('Please fill in all card details');
          setLoading(false);
          return;
        }
        
        // If payment method was saved, use it; otherwise use card_data
        if (paymentMethodIdToUse && paymentMethodIdToUse !== 'new') {
          payload.payment_method_id = paymentMethodIdToUse;
          payload.card_data = {
            cvv: formData.cvv,
          };
        } else {
          payload.card_data = {
            card_number: formData.card_number.replace(/\s/g, ''),
            expiry_month: parseInt(formData.expiry_month),
            expiry_year: parseInt(formData.expiry_year),
            cvv: formData.cvv,
            cardholder_name: formData.cardholder_name,
          };
        }
      } else if (paymentMethodIdToUse && paymentMethodIdToUse !== 'new') {
        // Using selected payment method
        if (!formData.cvv) {
          setError('Please enter CVV');
          setLoading(false);
          return;
        }
        payload.payment_method_id = paymentMethodIdToUse;
        // Only send CVV - backend will construct full card_data from payment method
        payload.card_data = {
          cvv: formData.cvv,
        };
      } else if (defaultPaymentMethod) {
        // Use default payment method (backend will handle this)
        if (!formData.cvv) {
          setError('Please enter CVV');
          setLoading(false);
          return;
        }
        payload.payment_method_id = defaultPaymentMethod.id;
        // Only send CVV - backend will construct full card_data from payment method
        payload.card_data = {
          cvv: formData.cvv,
        };
      } else {
        // No payment method selected and no default - this should not happen due to validation above
        setError('Please select a payment method or add a new one');
        setLoading(false);
        return;
      }

      const response = await api.post('/payments/process/', payload);

      if (response.data.message) {
        setSuccess(true);
        // Refresh payment methods in case a new one was added
        await fetchPaymentMethods();
        setTimeout(() => {
          navigate('/customer/invoices');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.details || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  if (loadingInvoice) {
    return (
      <Layout>
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (!currentInvoice) {
    return (
      <Layout>
        <Container>
          <Alert severity="error">No invoice selected. Please select an invoice to pay.</Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/customer/invoices')}
            sx={{ mt: 2 }}
          >
            Go to Invoices
          </Button>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Make Payment
        </Typography>
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box mb={3}>
            <Typography variant="h6">Invoice Details</Typography>
            <Typography>Invoice Number: {currentInvoice.invoice_number}</Typography>
            <Typography>Amount: ${currentInvoice.total_amount}</Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Payment processed successfully!
            </Alert>
          )}
          
          {loadingMethods ? (
            <Typography>Loading payment methods...</Typography>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Payment Method Selection */}
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel id="payment-method-label">Payment Method</InputLabel>
                    <Select
                      labelId="payment-method-label"
                      id="payment-method-select"
                      value={(() => {
                        // Only use a value that exists in the paymentMethods array
                        if (loadingMethods) return '';
                        
                        const validSelectedId = selectedPaymentMethodId && 
                          paymentMethods.length > 0 && 
                          paymentMethods.some(m => m.id === selectedPaymentMethodId)
                          ? selectedPaymentMethodId
                          : null;
                        
                        const validDefaultId = defaultPaymentMethod && 
                          paymentMethods.length > 0 && 
                          paymentMethods.some(m => m.id === defaultPaymentMethod.id)
                          ? defaultPaymentMethod.id
                          : null;
                        
                        return validSelectedId || validDefaultId || '';
                      })()}
                      onChange={handlePaymentMethodChange}
                      label="Payment Method"
                      required
                      displayEmpty={false}
                      disabled={loadingMethods}
                    >
                      {paymentMethods.length === 0 ? (
                        <MenuItem value="" disabled>
                          <Typography variant="body2" color="textSecondary">
                            No payment methods available
                          </Typography>
                        </MenuItem>
                      ) : (
                        <>
                          {/* Show all payment methods, with default first */}
                          {paymentMethods
                            .filter(method => method.is_active)
                            .sort((a, b) => {
                              // Sort: default first, then by creation date
                              if (a.is_default) return -1;
                              if (b.is_default) return 1;
                              return 0;
                            })
                            .map((method) => (
                              <MenuItem key={method.id} value={method.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <CreditCardIcon />
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body1">
                                        {method.masked_card || `•••• •••• •••• ${method.last_four_digits}`}
                                      </Typography>
                                      {method.is_default && (
                                        <Chip label="Default" size="small" color="primary" />
                                      )}
                                      {method.is_expired && (
                                        <Chip label="Expired" size="small" color="error" />
                                      )}
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                      {method.cardholder_name} • Expires {String(method.expiry_month).padStart(2, '0')}/{method.expiry_year}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MenuItem>
                            ))}
                        </>
                      )}
                      <MenuItem value="new">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AddIcon />
                          <Typography>Add New Payment Method</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {paymentMethods.length === 0 && !loadingMethods && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                      No payment methods found. Please add a payment method to continue.
                    </Typography>
                  )}
                </Grid>

                {/* New Card Form - Only shown when "Add New" is selected */}
                {showNewCardForm && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        New Payment Method
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Card Number"
                        name="card_number"
                        value={formData.card_number}
                        onChange={handleChange}
                        required
                        placeholder="1234 5678 9012 3456"
                        inputProps={{ maxLength: 19 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Cardholder Name"
                        name="cardholder_name"
                        value={formData.cardholder_name}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        select
                        SelectProps={{ native: true }}
                        label="Expiry Month"
                        name="expiry_month"
                        value={formData.expiry_month}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Month</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                          <option key={month} value={month}>
                            {String(month).padStart(2, '0')}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        select
                        SelectProps={{ native: true }}
                        label="Expiry Year"
                        name="expiry_year"
                        value={formData.expiry_year}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Year</option>
                        {Array.from({ length: 21 }, (_, i) => {
                          const year = new Date().getFullYear() + i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </TextField>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="CVV"
                        name="cvv"
                        type="password"
                        value={formData.cvv}
                        onChange={handleChange}
                        required
                        inputProps={{ maxLength: 4, minLength: 3 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center">
                        <input
                          type="checkbox"
                          id="save_payment_method"
                          name="save_payment_method"
                          checked={formData.save_payment_method}
                          onChange={(e) => setFormData({
                            ...formData,
                            save_payment_method: e.target.checked
                          })}
                        />
                        <label htmlFor="save_payment_method" style={{ marginLeft: 8 }}>
                          Save this payment method for future use
                        </label>
                      </Box>
                    </Grid>
                  </>
                )}

                {/* Display selected payment method details */}
                {!showNewCardForm && (selectedPaymentMethodId || defaultPaymentMethod) && (() => {
                  const currentMethodId = selectedPaymentMethodId || defaultPaymentMethod?.id;
                  const selectedMethod = paymentMethods.find(m => m.id === currentMethodId);
                  
                  if (!selectedMethod) return null;
                  
                  return (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>
                          Selected Payment Method
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ bgcolor: 'background.default', p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CreditCardIcon color="primary" sx={{ fontSize: 40 }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="h6">
                                  {selectedMethod.masked_card || `•••• •••• •••• ${selectedMethod.last_four_digits}`}
                                </Typography>
                                {selectedMethod.is_default && (
                                  <Chip label="Default" size="small" color="primary" />
                                )}
                                {selectedMethod.is_expired && (
                                  <Chip label="Expired" size="small" color="error" />
                                )}
                              </Box>
                              <Typography variant="body2" color="textSecondary">
                                {selectedMethod.cardholder_name}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Expires {String(selectedMethod.expiry_month).padStart(2, '0')}/{selectedMethod.expiry_year}
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="CVV"
                          name="cvv"
                          type="password"
                          value={formData.cvv}
                          onChange={handleChange}
                          required
                          inputProps={{ maxLength: 4, minLength: 3 }}
                          helperText="Enter CVV for your saved card"
                          autoComplete="off"
                        />
                      </Grid>
                    </>
                  );
                })()}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Amount"
                    name="amount"
                    type="number"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading || success}
                    size="large"
                  >
                    {loading ? 'Processing...' : 'Process Payment'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Container>
    </Layout>
  );
};

export default CustomerPayments;
